import { Link } from "@tanstack/react-router";
import { ArrowRight, MapPin } from "lucide-react";
import { trackEvent } from "@/lib/tracking";
import { areas as ALL_AREAS } from "@/lib/site-config";

interface Props {
  areaSlug: string;
  areaName: string;
  /** Unique 80-130 word area-specific intro. Falls back to a generic line. */
  intro?: string;
}

/**
 * Neighbour-area cluster — gives Google reciprocal area-to-area links
 * so Woodseer ranks for "garage near {area}" beyond just E14.
 */
const NEIGHBOURS: Record<string, string[]> = {
  poplar: ["canary-wharf", "limehouse", "bow", "blackwall"],
  "canary-wharf": ["poplar", "isle-of-dogs", "blackwall", "limehouse"],
  "isle-of-dogs": ["canary-wharf", "poplar", "blackwall"],
  limehouse: ["poplar", "whitechapel", "canary-wharf", "shadwell"],
  docklands: ["canning-town", "blackwall", "isle-of-dogs", "canary-wharf"],
  "tower-hamlets": ["bethnal-green", "whitechapel", "poplar", "mile-end"],
  whitechapel: ["brick-lane", "aldgate", "bethnal-green", "stepney"],
  "brick-lane": ["whitechapel", "bethnal-green", "aldgate"],
  bow: ["mile-end", "poplar", "bethnal-green"],
  "mile-end": ["bow", "bethnal-green", "whitechapel", "stepney"],
  "bethnal-green": ["brick-lane", "bow", "whitechapel", "mile-end"],
  wapping: ["shadwell", "limehouse", "aldgate"],
  stratford: ["bow", "canning-town", "mile-end"],
  stepney: ["mile-end", "whitechapel", "shadwell"],
  shadwell: ["limehouse", "whitechapel", "wapping"],
  aldgate: ["whitechapel", "brick-lane", "wapping"],
  "canning-town": ["blackwall", "canary-wharf", "docklands", "stratford"],
  blackwall: ["canary-wharf", "poplar", "isle-of-dogs", "docklands"],
};

/**
 * "Popular services for local drivers" block surfaced on area pages.
 * Pulls high-intent commercial searches (MOT, PCO/private hire, value
 * garage, diagnostics, brakes, tyres) into the area journey without
 * keyword-stuffing the page. Also surfaces neighbour areas for cluster
 * SEO and reciprocal internal-link signal.
 */
export function AreaDriverLinks({ areaSlug, areaName, intro }: Props) {
  const links: { to: string; label: string; sub: string }[] = [
    { to: "/mot", label: "MOT testing", sub: "Classes 1, 2, 3, 4, 5 & 7" },
    { to: "/pco-mot-east-london", label: "PCO / private-hire drivers", sub: "Garage support for work vehicles" },
    { to: "/uber-mot-east-london", label: "Uber MOT renewal", sub: "TfL inspection prep & retest" },
    { to: "/value-for-money-garage-east-london", label: "Value-for-money garage", sub: "Clear advice, fair pricing" },
    { to: "/diagnostics", label: "Diagnostics", sub: "Engine, ABS & dashboard warnings" },
    { to: "/diesel-dpf-egr-east-london", label: "Diesel DPF & EGR", sub: "DPF regen, cleans, EGR faults" },
    { to: "/google-maps-garage-e14", label: "Find us on Google Maps", sub: "Sat-nav, postcode & yard entrance" },
    { to: "/book", label: "Book MOT", sub: "Pick a slot — we confirm by phone" },
  ];

  const neighbourSlugs = NEIGHBOURS[areaSlug] ?? [];
  const neighbours = neighbourSlugs
    .map((s) => ALL_AREAS.find((a) => a.slug === s))
    .filter((a): a is { slug: string; name: string; description: string } => Boolean(a));

  return (
    <section className="py-12 md:py-14 bg-secondary/40">
      <div className="mx-auto max-w-5xl px-4">
        <div className="max-w-2xl">
          <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-primary">
            Popular with {areaName} drivers
          </p>
          <h2 className="mt-2 text-2xl md:text-3xl font-bold text-foreground">
            Popular services for local drivers
          </h2>
          <p className="mt-3 text-sm md:text-base text-muted-foreground">
            {intro ?? `Most ${areaName} customers come to us for MOTs, value-for-money repairs, PCO / private-hire support or a same-day brake check. Tap a service for full details and current advice.`}
          </p>
        </div>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() =>
                trackEvent("area_driver_link_click", { area: areaSlug, target: l.to })
              }
              className="group rounded-xl border border-border bg-card p-4 hover:border-primary/40 transition-colors"
            >
              <div className="flex items-start justify-between gap-2">
                <span className="text-sm font-bold text-foreground">{l.label}</span>
                <ArrowRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
              </div>
              <p className="mt-1 text-xs text-muted-foreground">{l.sub}</p>
            </Link>
          ))}
        </div>

        {neighbours.length > 0 && (
          <div className="mt-10 border-t border-border pt-6">
            <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-primary">
              Also near {areaName}
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              We look after drivers in the surrounding areas too — quick to reach from {areaName}.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {neighbours.map((n) => (
                <Link
                  key={n.slug}
                  to="/areas/$area"
                  params={{ area: n.slug }}
                  onClick={() =>
                    trackEvent("area_driver_link_click", {
                      area: areaSlug,
                      target: `/areas/${n.slug}`,
                      kind: "neighbour_area",
                    })
                  }
                  className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3.5 py-1.5 text-xs font-bold text-foreground hover:border-primary/40 hover:text-primary transition-colors"
                >
                  <MapPin className="h-3 w-3" />
                  Garage near {n.name}
                  <ArrowRight className="h-3 w-3" />
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
